import React from 'react';
import { Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import useCanUser from '@src/hooks/useCanUser';

/**
 * The "you can't see this" message shown in place of a viewer/user-level surface when the operator
 * has revoked the current user's `hasViewerAccess` permission (PDF-1524 — Disney compliance). Used
 * for in-page tabs and full-page routes alike so the treatment is consistent.
 */
export const NoViewerAccess = () => (
  <Result
    icon={<LockOutlined />}
    title="You don't have permission to view this section"
    subTitle="Contact your admin to request access."
  />
);

interface Props {
  children: React.ReactNode;
  /** what to render when access is denied; defaults to the standard NoViewerAccess message */
  fallback?: React.ReactNode;
}

/**
 * Gate viewer/user-level content on the per-user `hasViewerAccess` permission. Renders `children`
 * when allowed, otherwise the fallback (the NoViewerAccess message by default). Cosmetic layer —
 * the server (requireViewerAccess) is the real enforcement.
 */
export const ViewerAccessGate = ({ children, fallback }: Props) => {
  const hasViewerAccess = useCanUser('hasViewerAccess');
  if (!hasViewerAccess) {
    return <>{fallback ?? <NoViewerAccess />}</>;
  }
  return <>{children}</>;
};

export default ViewerAccessGate;
